import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider } from './contexts/AuthContext';
import { BookProvider } from './contexts/BookContext';
import { DummyDataProvider } from './contexts/DummyDataContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { DimensionProvider } from './contexts/DimensionContext';
import { StatusTrackingProvider } from './contexts/StatusTrackingContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CreatePage from './pages/CreatePage';
import EditPage from './pages/EditPage';
import DesignPage from './pages/DesignPage';
import WorldPage from './pages/WorldPage';
import PrintPage from './pages/PrintPage';
import ShipPage from './pages/ShipPage';
import LibraryPage from './pages/LibraryPage';
import PrintStatusPage from './pages/PrintStatusPage';
import PrintJobsPage from './pages/PrintJobsPage';
import PrintJobDetailsPage from './pages/PrintJobDetailsPage';
import PrintJobPaymentStatusPage from './pages/PrintJobPaymentStatusPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CreditsPage from './pages/account/CreditsPage';
import ActivityFeedPage from './pages/account/ActivityFeedPage';
import PurchaseHistoryPage from './pages/account/PurchaseHistoryPage';
import CouponRedemptionPage from './pages/account/CouponRedemptionPage';
import NotificationsPage from './pages/account/NotificationsPage';
import WizardLayout from './layouts/WizardLayout';
import MainLayout from './layouts/MainLayout';
import AccountLayout from './layouts/AccountLayout';
import AdminLayout from './layouts/AdminLayout';
import AdminPrintJobsPage from './pages/admin/AdminPrintJobsPage';
import AdminPrintJobDetailsPage from './pages/admin/AdminPrintJobDetailsPage';
import AdminPendingFulfillmentPage from './pages/admin/AdminPendingFulfillmentPage';
import AdminCompletedOrdersPage from './pages/admin/AdminCompletedOrdersPage';
import AdminCouponsPage from './pages/admin/AdminCouponsPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyCompletePage from './pages/VerifyCompletePage';
import VerifiedGuard from './components/Auth/VerifiedGuard';
import RedirectIfAuthenticated from './components/Auth/RedirectIfAuthenticated';

// Clear the single design status toast
window.clearDesignToasts = () => {
  // Dismiss the single design toast ID
  toast.dismiss('design-status-toast');
  
  // Store timestamp of clearing to prevent immediate re-shows
  localStorage.setItem('DESIGN_TOAST_CLEARED_TIME', Date.now());
  
  // Clear design-specific localStorage flag
  localStorage.removeItem('DESIGN_TOAST_SHOWN');
  
  // Clear the global flag
  window.DESIGN_READY_ACTIVE = false;
  
  console.log('Design status toast cleared');
};

// Global function to show a single design status toast
window.showDesignStatusToast = (id, status) => {
  // Use a consistent toast ID to ensure only one toast is shown
  const toastId = 'design-status-toast';
  
  // Status-based toast messages
  const messages = {
    generatingCharacter: 'Generating character images...',
    generatingCoverImage: 'Generating cover image...',
    updatingImageElements: 'Updating image elements...',
    completed: 'Design is ready!'
  };
  
  // If status is completed, don't show a toast
  if (status === 'completed') {
    toast.dismiss(toastId);
    return;
  }
  
  // Get message for the current status or use a default
  const message = messages[status] || 'Processing your book...';  
  
  // Update existing toast or show a new one
  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      render: message,
      autoClose: 8000
    });
  } else {
    toast.info(message, {
      toastId,
      position: "top-right",
      autoClose: 8000,
      closeOnClick: true,
      draggable: true,
      hideProgressBar: false,
      pauseOnHover: true
    });
  }
};

// Global function for persistent toast notifications across all pages
window.showPersistentToast = (message, options = {}) => {
  // Enforce a single toast and prevent duplicates
  const toastId = options.toastId || `global-toast-${Date.now()}`;
  
  // Don't show if already active with the same ID
  if (toast.isActive(toastId)) {
    return;
  }
  
  const toastOptions = {
    position: "top-right",
    autoClose: 8000, // Close after 8 seconds by default
    closeOnClick: true, // Allow closing with click
    draggable: true,
    progress: undefined,
    closeButton: true,
    ...options,
    toastId: toastId
  };

  toast.success(message, toastOptions);
};

// Updated function - shows toast for multiple image generation statuses
window.showDesignReadyToast = function(bookId) {
  console.log('Checking if toast should show for book:', bookId);
  
  if (!bookId) {
    console.log('No bookId provided');
    return;
  }
  
  // Get the current status
  let currentStatus = null;
  
  try {
    // First check if there's a getCurrentProcessingStatus function
    if (window.getCurrentProcessingStatus && typeof window.getCurrentProcessingStatus === 'function') {
      currentStatus = window.getCurrentProcessingStatus(bookId);
    }
    
    // Fallback to localStorage
    if (!currentStatus) {
      const statusData = localStorage.getItem(`bookStatus_${bookId}`);
      if (statusData) {
        const parsed = JSON.parse(statusData);
        currentStatus = parsed?.status;
      }
    }
  } catch (error) {
    console.error('Error getting book status:', error);
    return;
  }
  
  console.log(`Current status for book ${bookId}: ${currentStatus}`);
  
  // Show toast for all valid image generation statuses
  const validStatuses = ['generatingCharacter', 'generatingCoverImage', 'updatingImageElements'];
  
  if (validStatuses.includes(currentStatus)) {
    // Create a status-specific message
    let message = "Design is ready! ";
    
    if (currentStatus === 'generatingCoverImage') {
      message += "Creating your book cover image...";
    } else if (currentStatus === 'generatingCharacter') {
      message += "Generating character images...";
    } else if (currentStatus === 'updatingImageElements') {
      message += "Adding images to your book...";
    } else {
      message += "You can continue while images are being generated.";
    }
    
    // Show the toast with the specific message
    toast(message, {
      position: "top-right",
      autoClose: 7000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      toastId: `design-ready-toast-${currentStatus}`, // Use status-specific ID
    });
    console.log(`Toast shown for ${currentStatus} status`);
  } else {
    console.log(`No toast shown - status "${currentStatus}" is not a valid image generation status`);
  }
}

function App() {
  // Set up event listener for toast events
  React.useEffect(() => {
    // Create custom event listener for toast notifications
    const handleToastEvent = (event) => {
      const { message, options } = event.detail;
      window.showPersistentToast(message, options);
    };
    
    // Register event listener
    window.addEventListener('show-persistent-toast', handleToastEvent);
    
    return () => {
      window.removeEventListener('show-persistent-toast', handleToastEvent);
    };
  }, []);
  
  return (
    <Router>
      <DummyDataProvider>
        <AuthProvider>
          <CreditsProvider>
            <DimensionProvider>
              <StatusTrackingProvider>
                <BookProvider>
                <Routes>
                {/* Main Layout Routes */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<RedirectIfAuthenticated to="/library"><LoginPage /></RedirectIfAuthenticated>} />
                  <Route path="/register" element={<RedirectIfAuthenticated to="/library"><RegisterPage /></RedirectIfAuthenticated>} />
                  <Route path="/forgot-password" element={<RedirectIfAuthenticated to="/library"><ForgotPasswordPage /></RedirectIfAuthenticated>} />
                  <Route path="/verify-email" element={<RedirectIfAuthenticated to="/library"><VerifyEmailPage /></RedirectIfAuthenticated>} />
                  <Route path="/auth/verify-complete" element={<VerifyCompletePage />} />
                  <Route path="/checkout/success" element={<ProtectedRoute><VerifiedGuard><CheckoutSuccessPage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/print-status/:printJobId" element={<ProtectedRoute><VerifiedGuard><PrintStatusPage /></VerifiedGuard></ProtectedRoute>} />
                </Route>

                {/* Library with Account Layout (includes sidenav) */}
                <Route path="/library" element={<ProtectedRoute><VerifiedGuard><AccountLayout /></VerifiedGuard></ProtectedRoute>}>
                  <Route index element={<LibraryPage />} />
                </Route>

                {/* Wizard Layout Routes */}
                <Route element={<WizardLayout />}>
                  <Route path="/create" element={<ProtectedRoute><VerifiedGuard><CreatePage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/edit/:bookId" element={<ProtectedRoute><VerifiedGuard><EditPage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/world/:bookId" element={<ProtectedRoute><VerifiedGuard><WorldPage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/design/:bookId/:spreadId?" element={<ProtectedRoute><VerifiedGuard><DesignPage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/print/:bookId" element={<ProtectedRoute><VerifiedGuard><PrintPage /></VerifiedGuard></ProtectedRoute>} />
                  <Route path="/ship/:bookId" element={<ProtectedRoute><VerifiedGuard><ShipPage /></VerifiedGuard></ProtectedRoute>} />
                </Route>

                {/* Account Layout Routes */}
                <Route path="/account" element={<ProtectedRoute><VerifiedGuard><AccountLayout /></VerifiedGuard></ProtectedRoute>}>
                  <Route path="credits" element={<CreditsPage />} />
                  <Route path="subscription" element={<Navigate to="/account/credits" replace />} />
                  <Route path="coupons" element={<CouponRedemptionPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="activity" element={<ActivityFeedPage />} />
                  <Route path="purchases" element={<PurchaseHistoryPage />} />
                  <Route path="print-jobs" element={<PrintJobsPage />} />
                  <Route path="print-jobs/:jobId" element={<PrintJobDetailsPage />} />
                  <Route path="print-jobs/:jobId/payment-status" element={<PrintJobPaymentStatusPage />} />
                </Route>
                
                {/* Admin Layout Routes */}
                <Route path="/admin" element={<ProtectedRoute><VerifiedGuard><AdminLayout /></VerifiedGuard></ProtectedRoute>}>
                  <Route index element={<AdminPrintJobsPage />} />
                  <Route path="print-jobs" element={<AdminPrintJobsPage />} />
                  <Route path="print-jobs/:jobId" element={<AdminPrintJobDetailsPage />} />
                  <Route path="pending-fulfillment" element={<AdminPendingFulfillmentPage />} />
                  <Route path="completed-orders" element={<AdminCompletedOrdersPage />} />
                  <Route path="coupons" element={<AdminCouponsPage />} />
                </Route>
                </Routes>
                </BookProvider>
              </StatusTrackingProvider>
            </DimensionProvider>
          </CreditsProvider>
        </AuthProvider>
      </DummyDataProvider>
      {/* SIMPLE FIX: Regular toast container */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        limit={7}
      />
      
    </Router>
  );
}

export default App;